// src/contexts/UserContext.tsx

import React, { createContext, useContext, useState, useEffect } from "react";
import { getAuth, User } from "firebase/auth";
import { AppUser } from "../domain/types/AppUser";
import { firebaseApp, firebaseDB } from "../infrastructure/firebase/firebase";
import { onSnapshot, collection, query, where } from "firebase/firestore";
import {
  findByProviderUserId,
  getFirestoreSafeProviderName,
} from "../infrastructure/repositories/providerUserRepository";
import { ProviderUser } from "../domain/types/ProviderUser";

interface UserContextProps {
  currentUser: User | null;
  providerUser: ProviderUser | null;
  appUser: AppUser | null;
  loading: boolean;
  error: Error | null;
}

const UserContext = createContext<UserContextProps>({
  currentUser: null,
  providerUser: null,
  appUser: null,
  loading: true,
  error: null,
});

type UserProviderProps = {
  children: React.ReactNode;
};

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [providerUser, setProviderUser] = useState<ProviderUser | null>(null);
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const auth = getAuth(firebaseApp);

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      if (user) {
        try {
          const providerName = user.providerData[0].providerId;
          const providerUserId = user.uid;

          const providerField = `providerUserIds.${getFirestoreSafeProviderName(providerName)}`;

          const appUsersCollection = collection(firebaseDB, "appUsers");
          const appUserQuery = query(
            appUsersCollection,
            where(providerField, "array-contains", providerUserId),
          );

          const unsubscribeAppUser = onSnapshot(
            appUserQuery,
            async (querySnapshot) => {
              if (!querySnapshot.empty) {
                const appUserDoc = querySnapshot.docs[0];
                console.log("Fetched appUserData:", appUserDoc.data());
                setAppUser(appUserDoc.data() as AppUser);

                try {
                  const fetchedProviderUser =
                    await findByProviderUserId(providerUserId);
                  setProviderUser(fetchedProviderUser);
                } catch (err) {
                  console.error("Error fetching providerUser:", err);
                  setProviderUser(null);
                  setError(err as Error);
                }
              } else {
                setAppUser(null);
              }
              setLoading(false);
            },
            (error) => {
              console.error("Error listening to AppUser snapshot:", error);
              setError(error);
              setLoading(false);
            },
          );

          // Cleanup function to unsubscribe from snapshot listener
          return () => {
            unsubscribeAppUser();
          };
        } catch (err) {
          console.error("Error in UserProvider:", err);
          setError(err as Error);
          setLoading(false);
        }
      } else {
        setAppUser(null);
        setLoading(false);
      }
    });

    // Cleanup function for auth state listener
    return () => {
      unsubscribeAuth();
    };
  }, [auth]);

  const contextValue: UserContextProps = {
    currentUser,
    providerUser,
    appUser,
    loading,
    error,
  };

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
